import TransportationClient from '@/api/TransportationClient';
import TransportationGatewayClient from '@/api/TransportGatewayClient';

const defaultState = {
  jobs: [],
  pagination: {
    first: true,
    last: true,
    totalPages: 1,
    totalElements: 1,
    numberOfElements: 1,
    size: 100,
    number: 1,
  },
  selectedJob: null,
  filters: {
    sort: 'createdDate,desc',
    search: '',
    statuses: '',
  },
  neverRequested: null,
  isLoading: true,
  error: null,
};

const mutations = {
  setError(state, error) {
    state.error = error;
  },
  setSelectedJob(state, data) {
    state.selectedJob = data;
  },
  setJobsData(state, data) {
    state.jobs = data.content;
    state.pagination = data.pagination;
    state.isLoading = false;
  },
  addToJobsData(state, data) {
    state.jobs = [...state.jobs, ...data.content];
    state.pagination = data.pagination;
    state.isLoading = false;
  },
  setPageSizeAndNumber(state, { pageSize, currentPage }) {
    state.pagination.size = pageSize;
    state.pagination.number = currentPage;
  },
  setFilterSearch(state, { sort, search, statuses }) {
    const cleanup = {
      sort: sort || 'createdDate,desc',
      search: search || '',
      statuses: statuses || '',
    };
    state.filters = cleanup;
  },
  setNeverRequested(state, value) {
    state.neverRequested = value;
  },
  setLoadingStatus(state, value) {
    state.isLoading = value;
  },
  trimJobsList(state) {
    state.pagination.last = state.jobs.length > state.pagination.size;
    state.jobs = state.jobs.slice(0, state.pagination.size);
    state.pagination.first = true;
    state.pagination.number = 1;
  },
};

const actions = {
  async getJobs({ rootGetters, commit, state }, { append }) {
    commit('setLoadingStatus', true);
    const api = new TransportationClient(rootGetters.currentUser);

    const payload = {
      page: append ? state.pagination.number + 1 : state.pagination.number,
      size: state.pagination.size,
      sort: state.filters.sort,
    };

    if (state.filters.search.length > 0) {
      payload.search = state.filters.search;
    }
    if (state.filters.statuses.length > 0) {
      payload.statuses = state.filters.statuses;
    }

    const { data, detail } = await api.getTransportationRequests(payload);

    if (data.data) {
      if (append) {
        commit('addToJobsData', data.data);
      } else {
        commit('setJobsData', data.data);
      }

      const { search, statuses } = state.filters;

      const isWithoutFilters = statuses.length === 0 && search.length === 0;

      if (isWithoutFilters && data.data.content.length === 0) {
        commit('setNeverRequested', true);
      } else {
        commit('setNeverRequested', false);
      }

      return data.data;
    }
    return detail;
  },
  async updatePageSize({ rootGetters, commit }, { pageSize, currentPage }) {
    commit('setLoadingStatus', true);
    const api = new TransportationClient(rootGetters.currentUser);
    const payload = {
      page: currentPage,
      size: pageSize,
      sort: 'createdDate,desc',
      search: '',
      statuses: [''],
    };

    const { data, detail } = await api.getTransportationRequests(payload);

    if (data.data) {
      commit('setJobsData', data.data);
      return data.data;
    }
    return detail;
  },
  updateSearch({ commit }, search) {
    commit('setLoadingStatus', true);
    commit('setFilterSearch', search);
  },
  trimJobsList({ commit }) {
    commit('trimJobsList');
  },
  replaceGatePassUrlForSpecificJob({ commit, state }, { jobId, gatePassUrl }) {
    const { jobs } = state;

    const newJobsArray = jobs.map((item) => {
      if (item.id === jobId) {
        return { ...item, gatePassUrl };
      }

      return item;
    });

    commit('setJobsData', { content: newJobsArray, pagination: state.pagination });
  },
  setSelectedJob({ commit, state }, { jobId }) {
    const selectedJob = jobId ? state.jobs.find((job) => job.id === jobId) : null;

    commit('setSelectedJob', selectedJob);
  },
  async addGatePassToRequest({ commit, dispatch, rootGetters }, { jobId, fileObj }) {
    commit('setError', null);

    const { file } = fileObj;
    const api = new TransportationClient(rootGetters.currentUser);
    const apiGateway = new TransportationGatewayClient();

    try {
      const { data } = await api.uploadGatePass({ file });

      const { fileKey } = data.data;

      await apiGateway.addGatePassToRequest(jobId, fileKey);

      dispatch('replaceGatePassUrlForSpecificJob', { jobId, gatePassUrl: fileKey });
      dispatch('setSelectedJob', { jobId });
    } catch (e) {
      commit('setError', e);
    }
  },
  async removeGatePassFromRequest({ commit, dispatch }, { jobId }) {
    commit('setError', null);

    const apiGateway = new TransportationGatewayClient();
    try {
      await apiGateway.removeGatePass(jobId);

      dispatch('replaceGatePassUrlForSpecificJob', { jobId, gatePassUrl: '' });
      dispatch('setSelectedJob', { jobId });
    } catch (e) {
      commit('setError', e);
    }
  },
  resetErrorState({ commit }) {
    commit('setError', null);
  },
};

const getters = {
  jobs: (state) => () => state.jobs,
  selectedJob: (state) => () => state.selectedJob,
  error: (state) => () => state.error,
};

export default {
  state: defaultState,
  mutations,
  actions,
  getters,
};
